"use client";

import { Select } from "@base-ui-components/react/select";
import type { GeoLocale } from "@trydig/next/i18n";
import { clsx } from "@trydig/ui";
import { Suspense, useEffect, useRef } from "react";
import { Chevron } from "~/components/icons/Chevron";
import { Dropdown } from "~/features/checkout/Dropdown";
import { countryCodes } from "~/generated/countries";
import { useGeo } from "~/i18n/routing";
import { useLocation } from "~/lib/hooks/useCentra";
import styles from "./CountrySelector.module.css";

export interface CountrySelectorProps {
	className?: string;
}

const countryMap: Record<string, string> = {
	Norway: "Norge",
	Sweden: "Sverige",
	Denmark: "Danmark",
};

const UnsuspendedCountrySelector = ({ className }: CountrySelectorProps) => {
	const { data: location, isLoading } = useLocation();
	const { action, language: selectedLanguage } = useGeo();

	if (countryCodes.length === 0 || isLoading) return null;

	return (
		<div>
			<Select.Root
				value={location?.country}
				onValueChange={(value) => {
					action(`${selectedLanguage}-${value}` as GeoLocale);
				}}
			>
				<Select.Trigger className={clsx(styles.Trigger, className)}>
					<Select.Value>
						{(value) => {
							const country = countryCodes?.find((c) => c.country === value);
							if (country) {
								return (
									<span>
										{countryMap[country.name!] || country.country} / ({country.currency})
									</span>
								);
							}
							return <span>{value}</span>;
						}}
					</Select.Value>
					<Select.Icon className={styles.Icon}>
						<Chevron direction="down" />
					</Select.Icon>
				</Select.Trigger>

				<Select.Portal>
					<Select.Positioner className={styles.Positioner}>
						<Select.Popup className={styles.Popup}>
							<Select.Item className={styles.Item} disabled value={null}>
								<Select.ItemText>Select country</Select.ItemText>
							</Select.Item>
							{countryCodes?.map((country) => (
								<Select.Item className={styles.Item} key={country.country} value={country.country}>
									<Select.ItemText>
										{country.name} ({country.currency})
									</Select.ItemText>
								</Select.Item>
							))}
						</Select.Popup>
					</Select.Positioner>
				</Select.Portal>
			</Select.Root>
		</div>
	);
};

export const CountrySelector: typeof UnsuspendedCountrySelector = (props) => {
	return (
		<Suspense>
			<UnsuspendedCountrySelector {...props} />
		</Suspense>
	);
};

export interface CountryDropdownProps {
	onChange?: (value: string) => void;
	value: string;
}

const UnsuspendedCountryDropdown = ({ onChange, value }: CountryDropdownProps) => {
	const { action, pending, language: selectedLanguage } = useGeo();

	// useActionState's `action` has no onSuccess callback - the only signal we get
	// is `pending` flipping back to false once the navigation settles. Track the
	// value we just requested and fire onChange when that happens.
	const pendingValueRef = useRef<string | null>(null);
	const wasPendingRef = useRef(pending);

	useEffect(() => {
		if (wasPendingRef.current && !pending && pendingValueRef.current) {
			onChange?.(pendingValueRef.current);
			pendingValueRef.current = null;
		}
		wasPendingRef.current = pending;
	}, [pending, onChange]);

	if (countryCodes.length === 0) return null;

	return (
		<Dropdown
			options={
				countryCodes
					.map((c) => {
						if (!c.name || !c.country) return null;

						return {
							label: countryMap[c.name] || c.country,
							value: c.country,
						};
					})
					.filter(Boolean) as []
			}
			onChange={(e) => {
				pendingValueRef.current = (e?.value as string) ?? null;
				action(`${selectedLanguage}-${e?.value}` as GeoLocale);
			}}
			value={value}
		/>
	);
};

export const CountryDropdown: typeof UnsuspendedCountryDropdown = (props) => {
	return (
		<Suspense>
			<UnsuspendedCountryDropdown {...props} />
		</Suspense>
	);
};
